import { storeToRefs } from "pinia";
import type { RouteLocationNormalizedGeneric } from "vue-router";

import useProductRouting from "shared/composables/useProductRouting";
import features from "shared/services/features";
import Permissions from "shared/services/Permissions";
import { useGlobalStore } from "shared/stores/global";
import type { Nullable } from "shared/types";
import type { PermissionKey } from "shared/types/permissions";

import { type App } from "./app";
import { getLocaleText } from "./i18n";

let permissions: Permissions;
let appInstance: App;

const { routeProductName } = useProductRouting();

const getCurrentRoute = (): RouteLocationNormalizedGeneric =>
  appInstance.config.globalProperties.$route;

interface OrganisationFeatures {
  canManageBrandProfiles: () => boolean;
  canManageDistributionLists: () => boolean;
  canManageOutreach: () => boolean;
  canManageUsers: () => boolean;
  canViewOrganisationUsage: () => boolean;
  canViewRateCard: () => boolean;
}

interface MediaFeatures {
  canViewMediaAndTranscriptRequests: () => boolean;
  canViewMediaItems: () => boolean;
  canViewTranscriptRequests: () => boolean;
  hasKinetiqBroadcast: () => boolean;
  hasTvEyesBroadcast: () => boolean;
}

interface SocialFeatures {
  canAddSocialStream: () => boolean;
  canManageSocialEngage: () => boolean;
  canViewSocialAnalyse: () => boolean;
  canViewSocialEngage: () => boolean;
  canViewSocialListen: () => boolean;
  canViewSocialPlatform: () => boolean;
  hasSocialPermission: (permission: PermissionKey) => boolean;
}

interface CrmFeatures {
  canViewCrmPlatform: () => boolean;
}

interface OutreachActionStatus {
  isDisabled: boolean;
  tooltipText: Nullable<string>;
}

interface OutreachFeatures {
  actionStatus: (
    isFeatureEnabled: boolean,
    isPermissionEnabled: boolean
  ) => OutreachActionStatus;
  activities: () => OutreachActionStatus;
  canViewOutreachPlatform: () => boolean;
  contactLists: () => OutreachActionStatus;
  contactPrivateData: () => OutreachActionStatus;
  createActivities: () => OutreachActionStatus;
  defaultRouteName: (to: RouteLocationNormalizedGeneric) => string;
  hasAiPitch: () => boolean;
  hasDiscover: () => boolean;
  hasOutreachPermission: (permission: PermissionKey) => boolean;
  mediaReleaseAnalytics: () => OutreachActionStatus;
  quickSearch: () => OutreachActionStatus;
  search: () => OutreachActionStatus;
  sendMediaRelease: () => OutreachActionStatus;
  suggestedContacts: () => OutreachActionStatus;
}

interface TeaserFeatures {
  canViewOutreachPlatformTeaser: () => boolean;
  canViewSocialPlatformTeaser: () => boolean;
}

const isInsightsProduct = (): boolean =>
  routeProductName(getCurrentRoute()) === "insights";

const isMediaProduct = (): boolean =>
  routeProductName(getCurrentRoute()) === "media";

const isSocialProduct = (): boolean =>
  routeProductName(getCurrentRoute()) === "social";

const isOutreachProduct = (): boolean =>
  routeProductName(getCurrentRoute()) === "outreach";

const isDashboardProduct = (): boolean =>
  routeProductName(getCurrentRoute()) === "dashboard";

export const organisationFeatures: OrganisationFeatures = {
  canManageBrandProfiles(): boolean {
    return (
      ((isMediaProduct() || isDashboardProduct() || isInsightsProduct()) &&
        permissions.has("can_manage_brand_profiles")) ||
      (isSocialProduct() &&
        permissions.has("can_manage_brand_profiles", "social")) ||
      (isOutreachProduct() &&
        permissions.has("can_manage_brand_profiles", "outreach"))
    );
  },
  canManageDistributionLists(): boolean {
    return (
      ((isMediaProduct() || isDashboardProduct()) &&
        permissions.has("can_manage_distribution_lists")) ||
      (isOutreachProduct() &&
        permissions.has("can_manage_distribution_lists", "outreach"))
    );
  },
  canManageOutreach(): boolean {
    return (
      features.has("has_outreach") &&
      permissions.has("can_view_quick_search_and_contacts", "outreach")
    );
  },
  canManageUsers(): boolean {
    return (
      ((isMediaProduct() || isDashboardProduct()) &&
        permissions.has("can_manage_organisation_users")) ||
      (isSocialProduct() &&
        permissions.has("can_manage_organisation_users", "social")) ||
      (isOutreachProduct() &&
        permissions.has("can_manage_organisation_users", "outreach"))
    );
  },
  canViewOrganisationUsage(): boolean {
    return (
      ((isMediaProduct() || isDashboardProduct()) &&
        permissions.has("can_view_organisation_usage")) ||
      (isSocialProduct() &&
        permissions.has("can_view_organisation_usage", "social")) ||
      (isOutreachProduct() &&
        permissions.has("can_view_organisation_usage", "outreach"))
    );
  },
  canViewRateCard(): boolean {
    return (
      features.hasAny([
        "media_request",
        "audio_transcript",
        "has_instant_edit_transcript",
        "has_instant_edit_media_item",
      ]) && permissions.has("can_view_ratecard")
    );
  },
};

export const mediaFeatures: MediaFeatures = {
  canViewMediaAndTranscriptRequests(): boolean {
    const { isCisionOne } = storeToRefs(useGlobalStore());

    if (isCisionOne.value) {
      return (
        mediaFeatures.hasKinetiqBroadcast() ||
        mediaFeatures.hasTvEyesBroadcast()
      );
    }

    return (
      mediaFeatures.canViewMediaItems() &&
      mediaFeatures.canViewTranscriptRequests()
    );
  },
  canViewMediaItems(): boolean {
    return features.hasAny(["media_request", "has_instant_edit_media_item"]);
  },
  canViewTranscriptRequests(): boolean {
    return features.hasAny([
      "audio_transcript",
      "has_instant_edit_transcript",
      "has_completed_transcripts_page",
    ]);
  },
  hasKinetiqBroadcast(): boolean {
    return features.has("has_kinetiq_broadcast");
  },
  hasTvEyesBroadcast(): boolean {
    return features.hasAny([
      "has_tveyes_broadcast_anz",
      "has_tveyes_broadcast_us_canada",
      "has_tveyes_broadcast_uk",
      "has_tveyes_broadcast_europe",
      "has_tveyes_broadcast_rest_of_world",
      "has_tveyes_broadcast_canada",
    ]);
  },
};

export const socialFeatures: SocialFeatures = {
  canAddSocialStream(): boolean {
    return (
      socialFeatures.canViewSocialListen() &&
      socialFeatures.hasSocialPermission("can_create_mention_stream")
    );
  },
  canManageSocialEngage(): boolean {
    return (
      socialFeatures.canViewSocialEngage() &&
      socialFeatures.hasSocialPermission("can_approve_social_engage_posts")
    );
  },
  canViewSocialAnalyse(): boolean {
    return (
      appInstance.config.globalProperties.$isDesktop === true &&
      features.has("has_social_platform") &&
      socialFeatures.hasSocialPermission("can_use_analyse")
    );
  },
  canViewSocialEngage(): boolean {
    return (
      appInstance.config.globalProperties.$isDesktop === true &&
      features.has("has_social_engage") &&
      socialFeatures.hasSocialPermission("can_use_engage")
    );
  },
  canViewSocialListen(): boolean {
    return (
      features.has("has_social_platform") &&
      socialFeatures.hasSocialPermission("can_use_listen")
    );
  },
  canViewSocialPlatform(): boolean {
    return (
      socialFeatures.canViewSocialAnalyse() ||
      socialFeatures.canViewSocialListen() ||
      socialFeatures.canViewSocialEngage()
    );
  },
  hasSocialPermission(permission: PermissionKey): boolean {
    return permissions.has(permission, "social");
  },
};

export const crmFeatures: CrmFeatures = {
  canViewCrmPlatform(): boolean {
    return (
      features.has("has_journalist_enquiries") &&
      permissions.has("can_manage_crm")
    );
  },
};

export const outreachFeatures: OutreachFeatures = {
  actionStatus(
    isFeatureEnabled: boolean,
    isPermissionEnabled: boolean
  ): OutreachActionStatus {
    const result: OutreachActionStatus = {
      isDisabled:
        !appInstance.config.globalProperties.$isDesktop ||
        !isFeatureEnabled ||
        !isPermissionEnabled,
      tooltipText: null,
    };

    if (!isFeatureEnabled) {
      if (features.has("has_outreach_search_only")) {
        result.tooltipText = getLocaleText(
          "outreach.tooltips.search_only_upgrade"
        );
      } else {
        result.tooltipText = getLocaleText(
          "outreach.tooltips.media_release_upgrade"
        );
      }
    } else if (!isPermissionEnabled) {
      result.tooltipText = getLocaleText("outreach.tooltips.no_permission");
    }

    return result;
  },
  activities(): OutreachActionStatus {
    return this.actionStatus(
      features.has("has_outreach_activities_lists_search"),
      permissions.has("can_view_activities", "outreach")
    );
  },
  canViewOutreachPlatform(): boolean {
    return (
      appInstance.config.globalProperties.$isDesktop === true &&
      features.has("has_outreach") &&
      organisationFeatures.canManageOutreach()
    );
  },
  contactLists(): OutreachActionStatus {
    return this.actionStatus(
      features.has("has_outreach_activities_lists_search") ||
        features.has("has_outreach_mailing_lists"),
      permissions.has("can_manage_contact_list", "outreach")
    );
  },
  contactPrivateData(): OutreachActionStatus {
    return this.actionStatus(
      features.has("has_outreach_activities_lists_search"),
      permissions.has("can_view_activities", "outreach") &&
        permissions.has("can_manage_contact_list", "outreach")
    );
  },
  createActivities(): OutreachActionStatus {
    return this.actionStatus(
      features.has("has_outreach_activities_lists_search"),
      permissions.has("can_create_activities", "outreach")
    );
  },
  defaultRouteName(to: RouteLocationNormalizedGeneric): string {
    return to.params.product === "outreach"
      ? "outreach-launchpad"
      : "launchpad";
  },
  hasAiPitch(): boolean {
    return features.has("has_outreach_ai_pitch");
  },
  hasDiscover(): boolean {
    return features.has("has_outreach_discover");
  },
  hasOutreachPermission(permission: PermissionKey): boolean {
    return permissions.has(permission, "outreach");
  },
  mediaReleaseAnalytics(): OutreachActionStatus {
    return this.actionStatus(
      features.has("has_outreach_media_releases"),
      permissions.has("can_view_media_release_and_analytics", "outreach")
    );
  },
  quickSearch(): OutreachActionStatus {
    return this.actionStatus(
      features.has("has_outreach_search_only"),
      permissions.has("can_view_quick_search_and_contacts", "outreach")
    );
  },
  search(): OutreachActionStatus {
    return this.actionStatus(
      features.has("has_outreach_activities_lists_search"),
      permissions.has("can_view_quick_search_and_contacts", "outreach")
    );
  },
  sendMediaRelease(): OutreachActionStatus {
    return this.actionStatus(
      features.has("has_outreach_media_releases"),
      permissions.has("can_send_media_release", "outreach")
    );
  },
  suggestedContacts(): OutreachActionStatus {
    return this.actionStatus(
      features.has("has_outreach_profile_suggested_contacts"),
      permissions.has("can_manage_contact_list", "outreach")
    );
  },
};

export const teaserFeatures: TeaserFeatures = {
  canViewOutreachPlatformTeaser(): boolean {
    return (
      appInstance.config.globalProperties.$isDesktop === true &&
      features.has("has_feature_teasers") &&
      !features.has("has_outreach") &&
      permissions.has("can_manage_outreach")
    );
  },
  canViewSocialPlatformTeaser(): boolean {
    return (
      appInstance.config.globalProperties.$isDesktop === true &&
      features.has("has_feature_teasers") &&
      !features.has("has_social_platform")
    );
  },
};

export default ({ app }: { app: App }): void => {
  permissions = new Permissions();
  appInstance = app;

  Object.assign(app.config.globalProperties, {
    $mediaFeatures: mediaFeatures,
    $organisationFeatures: organisationFeatures,
    $outreachFeatures: outreachFeatures,
    $socialFeatures: socialFeatures,
    $teaserFeatures: teaserFeatures,
  });
};
